import ServiceListCardWrapper from '@/components/elements/Card/ServiceListCard/ServiceListCardWrapper';
import { LinkButton } from '@/components/elements/forms/buttons';
import { track } from '@/helpers/tracking';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { NormalizedPlace } from '@/types/place';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

export function trackGiftcardClick(company: { id: number; name: string; sellsValueCard: boolean }) {
  track('Gift Card Purchase Initiated', {
    place_id: String(company.id),
    place_name: company.name,
    // A place selling value cards links to a page where the user picks the card type.
    ...(!company.sellsValueCard && { gift_card_type: 'placecard' as const }),
    initiated_from: 'place_details',
  });
}

const baseTranslationKey = 'features.PlacePages.modules.Giftcards';

const Giftcards = ({ placeId }: { placeId: number }) => {
  const history = useHistory();
  const place = useSelector((state: { place: Record<number, NormalizedPlace> }) => state.place[placeId]);

  const normalizedPlaceAboutSettings = useSelector(
    (state: { place: Record<number, NormalizedPlace> }) => state.place[placeId]?.about?.settings,
  );

  const normalizedPlaceAboutSlug = useSelector(
    (state: { place: Record<number, NormalizedPlace> }) => state.place[placeId]?.about?.slug,
  );

  const getGiftcardLabel = () => {
    if (normalizedPlaceAboutSettings.sellsGiftCard && normalizedPlaceAboutSettings.sellsValueCard) {
      return _t('giftcardLabel') + ' / ' + _t('valuecardLabel');
    }

    if (normalizedPlaceAboutSettings.sellsGiftCard) {
      return _t('giftcardLabel');
    }

    if (normalizedPlaceAboutSettings.sellsValueCard) {
      return _t('valuecardLabel');
    }

    return '';
  };

  const getGiftcardCheckoutLink = () => {
    if (!normalizedPlaceAboutSettings?.sellsGiftCard && !normalizedPlaceAboutSettings?.sellsValueCard) {
      return null;
    }
    let url = `/places/${normalizedPlaceAboutSlug}-${placeId}`;

    if (normalizedPlaceAboutSettings.sellsValueCard) {
      url += '/valuecards/buy';
    } else {
      url += '/giftcard/checkout';
    }
    return url;
  };

  const { isMobileView } = useMobileView();
  const giftcardLabel = getGiftcardLabel();
  const giftcardLink = getGiftcardCheckoutLink();
  const trackedCompany = { id: place.id, name: place.about.name, sellsValueCard: place.about.settings.sellsValueCard };

  const handleRowGiftcardClick = () => {
    trackGiftcardClick(trackedCompany);
    history.push({ pathname: getGiftcardCheckoutLink() });
  };

  const handleLinkGiftcardClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    trackGiftcardClick(trackedCompany);
  };

  return (
    <section className="space-y-md">
      <h2 className="text-h-m font-semibold">{_t('GiftCard')}</h2>
      <div className="gap-sm flex flex-col">
        <ServiceListCardWrapper
          name={giftcardLabel}
          description={_t(`${baseTranslationKey}.card-description`)}
          subtTitle={<p className="text-m text-fg_secondary">{_t(`${baseTranslationKey}.card-description`)}</p>}
          price={{ type: 'optional', value: [0] }}
          onRowClick={handleRowGiftcardClick}
          rightSlot={
            <LinkButton
              to={giftcardLink}
              onClick={handleLinkGiftcardClick}
              variant="primary"
              size={isMobileView ? 'md' : 'sm'}
              className="flex max-w-[75px] items-center">
              {isMobileView ? _t('Buy') : _t('BuyNow')}
            </LinkButton>
          }
        />
      </div>
    </section>
  );
};

export default Giftcards;
